import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import styled, { css } from 'styled-components'
import { Check } from 'svgIcons'
import {
  GqlInterestedCompaniesDocument,
  GqlUserScrapCountsDocument,
  useGqlInterestedCompaniesQuery,
  useGqlInterestedCompaniesTotalCountQuery,
  useGqlRemoveAllCompanyInterestedInMutation,
} from 'generated/graphql'
import Button from '@app.components/Button'
import AnalysisResultItemSkeleton from '@app.feature/companyAnalysis/components/AnalysisResultItemSkeleton'
import { TCompanyInfoCard } from '@app.feature/companyInfo/main/type/companyInfoCard'
import useIsWebview from '@app.modules/hooks/useIsWebview'
import { getWebviewHref } from '@app.modules/util'
import useFlutterInappwebview from 'hooks/useFlutterInappwebview'
import Pagination from '../../../../../components/Pagination'
import { useScrapStateContext } from '../../../../../screens/ScrapScreen/useScrapState'
import CompanyScrapEmpty from '../../component/CompanyScrapEmpty'
import CompanyScrapItem from '../../component/CompanyScrapItem'
import ScrapDeleteAllModal from '../../component/ScrapDeleteAllModal'

const ITEMS_PER_PAGE = 20

const CompanyScrapList = () => {
  const router = useRouter()
  const isWebview = useIsWebview()
  const { fluterInappwebview } = useFlutterInappwebview()
  const [showDeleteAlert, setShowDeleteAlert] = useState(false)
  const context = useScrapStateContext()

  const { pagination } = context

  const [removeAllCompanyInterestedIn] =
    useGqlRemoveAllCompanyInterestedInMutation()

  const { data, loading, error } = useGqlInterestedCompaniesQuery({
    variables: {
      filterBy: {
        isRecruiting: router.query.nowHiring === 'true',
      },
      pagination: {
        page: Number(router.query.p) || 1,
        pageSize: 20,
      },
    },
    fetchPolicy: 'cache-and-network',
  })

  const { data: totalCountData } = useGqlInterestedCompaniesTotalCountQuery({
    variables: {
      filterBy: {},
    },
    fetchPolicy: 'cache-and-network',
  })

  const totalCompanyCount = totalCountData?.interestedCompanies.totalCount || 0

  const interestedCompaniesData: TCompanyInfoCard[] =
    data?.interestedCompanies.nodes?.map((company) => ({
      companyId: company.id,
      companyInformationId: company.companyInformation?.id || '',
      companyName: company.companyInformation?.company?.name || '',
      companyLogo: company.companyInformation?.thumbnailImage?.url || '',
      isHiring: company.companyInformation?.isRecruiting || false,
      isLiked: company.companyInformation?.viewerInterestedIn || false,
      companyType: company.companyInformation?.companyType || '',
      industryType:
        company.companyInformation?.companyIndustry?.secondary || '',
      likesCount: company.interestCount,
      viewsCount: company.viewCount,
    })) || []

  const totalCount = data?.interestedCompanies.totalCount || 0

  useEffect(() => {
    if (totalCount > 0) {
      const numberOfPages = Math.ceil(totalCount / ITEMS_PER_PAGE)
      pagination.setNumberOfPages(numberOfPages)
    }
  }, [totalCount, pagination])

  const deleteAllCompanyScraps = async () => {
    const { p: _, ...rest } = router.query
    router.push({
      pathname: router.pathname,
      query: {
        ...rest,
      },
    })
    await removeAllCompanyInterestedIn({
      refetchQueries: [
        GqlInterestedCompaniesDocument,
        GqlUserScrapCountsDocument,
      ],
    })
  }

  const handleConfirm = async () => {
    await deleteAllCompanyScraps()
    setShowDeleteAlert(false)
    if (isWebview) {
      fluterInappwebview?.callHandler('reportRemoveAllScraps')
    }
  }

  const handleCloseDeleteAlert = () => {
    setShowDeleteAlert(false)
  }

  const handleClickDeleteAll = () => {
    setShowDeleteAlert(true)
  }

  const handleChangeNowHiring = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { p: _, ...rest } = router.query
    router.push({
      pathname: router.pathname,
      query: {
        ...rest,
        nowHiring: e.target.checked,
      },
    })
  }

  if (error) {
    return <CompanyScrapEmpty />
  }

  return (
    <StyledWrapper>
      <div className="button-list">
        <Link
          href={{
            pathname: getWebviewHref('/calendar', isWebview),
            query: { activityType: 'activity', tab: 'myActivity' },
          }}
        >
          <Button className="calandar-btn" color="black" variant="outlined">
            내 스크랩 달력
          </Button>
        </Link>

        <Button
          className="delete-btn"
          color="lightGrey"
          disabled={data?.interestedCompanies.totalCount === 0}
          onClick={handleClickDeleteAll}
        >
          모두삭제
        </Button>
      </div>
      <div className="company-scrap-list-wrapper">
        <div className="header">
          <label htmlFor="now-hiring-checkbox">
            <input
              type="checkbox"
              id="now-hiring-checkbox"
              checked={router.query.nowHiring === 'true'}
              onChange={handleChangeNowHiring}
            />
            <span>
              <span className="checkbox-icon">
                {router.query.nowHiring === 'true' && (
                  <Check width={12} height={9} color="white" />
                )}
              </span>
              채용중
            </span>
          </label>
        </div>
        {!interestedCompaniesData.length || error ? (
          <CompanyScrapEmpty />
        ) : (
          <ul className="list-wrapper">
            {loading
              ? Array.from({ length: 5 }, (_, index) => (
                  <AnalysisResultItemSkeleton key={index} />
                ))
              : interestedCompaniesData.map((item) => (
                  <CompanyScrapItem
                    key={item.companyInformationId}
                    data={item}
                  />
                ))}
          </ul>
        )}
      </div>
      {totalCount > 0 && <Pagination pagination={pagination} />}
      <ScrapDeleteAllModal
        open={showDeleteAlert}
        onClose={handleCloseDeleteAlert}
        onConfirm={handleConfirm}
      >
        <DeleteDialogContent>
          <div className="delete-modal-content">
            잠깐, 관심설정한
            <span className="primary-text">
              {' '}
              {totalCompanyCount}개의 기업정보
            </span>
            를<br /> 모두 삭제하실건가요?
          </div>
        </DeleteDialogContent>
      </ScrapDeleteAllModal>
    </StyledWrapper>
  )
}

export default CompanyScrapList

const StyledWrapper = styled.div`
  ${({ theme }) => css`
    margin-top: 0px;

    .button-list {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      margin: 0;
      height: 0px;
      padding: 0 20px;
      gap: 0px;
      .calandar-btn,
      .delete-btn {
        width: inherit;
        height: 36px;
        transform: translate(0, -72px);
      }
      .calandar-btn {
        padding: 8px 13px;
        margin-right: 16px;
        font-size: 12px;
        letter-spacing: -0.2px;
      }
      .delete-btn {
        width: 81.77px;
        height: 40px;
        font-size: 12px;
        letter-spacing: -0.2px;
        padding: 0 5px;
        font-weight: 400;
      }
    }

    .company-scrap-list-wrapper {
      background-color: ${theme.background.bg_8};
      padding: 0 20px;
    }

    .header {
      height: 58px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid ${theme.line.line_3};

      > label {
        display: flex;
        align-items: center;
        cursor: pointer;
        font-size: 13px;

        & > input[type='checkbox'] {
          display: none;
        }

        & > span {
          position: relative;
          padding-left: 28px;
          display: flex;
          align-items: center;

          .checkbox-icon {
            position: absolute;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            display: flex;
            align-items: center;
            justify-content: center;
            width: 23px;
            height: 23px;
            border: 1.5px solid ${theme.line.line_2};
            border-radius: 5px;
            background-color: ${theme.background.bg_8};
            box-sizing: border-box;
            transition: all 0.2s;
          }
        }

        & > input[type='checkbox']:checked ~ span {
          color: ${theme.color.text_1};
          font-weight: 500;

          .checkbox-icon {
            background-color: ${theme.color.primary_2};
            border-color: ${theme.color.primary_2};
          }
        }
      }
    }
  `}
`

const DeleteDialogContent = styled.div`
  text-align: center;
  .primary-text {
    font-size: inherit;
    font-weight: inherit;
    color: #01a0ff;
  }
`
